import logger from '@/lib/logger';
import { create } from 'zustand';
import type { SavedImpedanceData } from '@/features/diagnostics/types';

interface DiagnosticsStore {
  savedBaselines: SavedImpedanceData[];
  savedMeasurements: SavedImpedanceData[];
  isRecording: boolean;
  recordingStartedAt: Date | null;
  
  addBaseline: (name: string, data: SavedImpedanceData['data']) => void;
  addMeasurement: (name: string, data: SavedImpedanceData['data']) => void;
  removeBaseline: (id: string) => void;
  removeMeasurement: (id: string) => void;
  clearBaselines: () => void;
  clearMeasurements: () => void;
  
  startRecording: () => void;
  stopRecording: () => void;
}

// Load from localStorage
const loadFromStorage = (key: string): SavedImpedanceData[] => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return parsed.map((item: any) => ({
      ...item,
      timestamp: new Date(item.timestamp)
    }));
  } catch {
    return [];
  }
};

// Save to localStorage
const saveToStorage = (key: string, data: SavedImpedanceData[]) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    logger.error(`Failed to save ${key} to localStorage:`, error);
  }
};

export const useDiagnosticsStore = create<DiagnosticsStore>((set) => ({
  savedBaselines: loadFromStorage('ediphor-test-baselines'),
  savedMeasurements: loadFromStorage('ediphor-test-measurements'),
  isRecording: false,
  recordingStartedAt: null,

  addBaseline: (name, data) => {
    set((state) => {
      const newBaseline: SavedImpedanceData = {
        id: crypto.randomUUID(),
        name,
        timestamp: new Date(),
        data
      };
      const updated = [...state.savedBaselines, newBaseline];
      saveToStorage('ediphor-test-baselines', updated);
      return { savedBaselines: updated };
    });
  },

  addMeasurement: (name, data) => {
    set((state) => {
      const newMeasurement: SavedImpedanceData = {
        id: crypto.randomUUID(),
        name,
        timestamp: new Date(),
        data
      };
      const updated = [...state.savedMeasurements, newMeasurement];
      saveToStorage('ediphor-test-measurements', updated);
      return { savedMeasurements: updated };
    });
  },

  removeBaseline: (id) => {
    set((state) => {
      const updated = state.savedBaselines.filter(item => item.id !== id);
      saveToStorage('ediphor-test-baselines', updated);
      return { savedBaselines: updated };
    });
  },

  removeMeasurement: (id) => {
    set((state) => {
      const updated = state.savedMeasurements.filter(item => item.id !== id);
      saveToStorage('ediphor-test-measurements', updated);
      return { savedMeasurements: updated };
    });
  },

  clearBaselines: () => {
    set({ savedBaselines: [] });
    localStorage.removeItem('ediphor-test-baselines');
  },

  clearMeasurements: () => {
    set({ savedMeasurements: [] });
    localStorage.removeItem('ediphor-test-measurements');
  },

  startRecording: () => {
    set({ isRecording: true, recordingStartedAt: new Date() });
  },

  stopRecording: () => {
    set({ isRecording: false });
  }
}));
